import { useCallback, useMemo, useRef, useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import { FiFilter } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import useInitialQueryData from "admin/Filters/useInitialQueryData";
import { joinTagFilter, splitTagFilter } from "components/v2/TagFilterList";
import { usePagedTags } from "api/TagResource/useTags";
import useGenres from "api/GenreResource/useGenres";
import { useBookFilterContext } from "./BookFilterContext";
import { formData, filteringParams } from "./booksQueryParams";
import { PillChoices, SheetSelect } from "./FilterControls";

const PRICING_OPTIONS = [
	{ value: "sellable", label: "Purchase only" },
	{ value: "subscription", label: "Subscription only" },
	{ value: "all", label: "Purchase & subscription" },
	{ value: "free", label: "Free" },
];

const ACTIVE_OPTIONS = [
	{ value: "1", label: "Active" },
	{ value: "0", label: "Inactive" },
];

const MISSING_FILE_OPTIONS = [
	{ value: "book_cover", label: "Missing book cover" },
	{ value: "book_file", label: "Missing book file" },
	{ value: "all", label: "Missing both files" },
];

const AVAILABLE_FILE_OPTIONS = [
	{ value: "book_cover", label: "With book cover" },
	{ value: "book_file", label: "With book file" },
	{ value: "all", label: "With both files" },
];

const countDraftFilters = (values) =>
	filteringParams.filter((key) => {
		const value = values?.[key];
		return value !== undefined && value !== null && String(value).trim() !== "";
	}).length;

const snapshotFromFilters = (filters) => {
	const next = {};
	Object.keys(formData).forEach((key) => {
		next[key] = filters?.[key] || "";
	});
	return next;
};

const BookFilter = ({
	genres,
	categories,
	publishers,
	numberOfActiveFilters = 0,
	hideFilters = [],
}) => {
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const [visible, setVisible] = useState(false);
	const [tagSearch, setTagSearch] = useState("");
	const initialDraftRef = useRef(null);
	const { filters } = useBookFilterContext();
	const { filterValues, setFilterValues } = useInitialQueryData(filters, formData);
	const { data: fetchedGenres } = useGenres("yes");

	const pagedTags = usePagedTags({
		fromPortal: "yes",
		search: tagSearch,
		enabled: visible,
		rowsPerPage: 40,
		sort: "usage",
	});

	const showPublisher =
		!hideFilters.includes("publisher") &&
		hasRoles([Role.SuperAdmin, Role.Admin, Role.Institution, Role.Educator]);

	const showStatusPricing = hasRoles([
		Role.SuperAdmin,
		Role.Admin,
		Role.SeniorPublisher,
		Role.Publisher,
	]);

	const publisherOptions = useMemo(
		() =>
			(publishers?.data || []).map((publisher) => ({
				value: publisher.id?.toString(),
				label: publisher.name,
			})),
		[publishers],
	);

	const genreList = useMemo(
		() => genres?.data?.data || fetchedGenres?.data?.data || [],
		[genres, fetchedGenres],
	);

	const categoryOptions = useMemo(
		() =>
			(categories?.data || []).map((category) => ({
				value: category.name,
				label: category.name,
			})),
		[categories],
	);

	const selectedGenres = useMemo(
		() => splitTagFilter(filterValues?.genreIs),
		[filterValues?.genreIs],
	);

	const selectedTags = useMemo(() => splitTagFilter(filterValues?.tagIs), [filterValues?.tagIs]);

	const draftFilterCount = useMemo(() => countDraftFilters(filterValues), [filterValues]);

	const isDirty = useMemo(() => {
		if (!initialDraftRef.current) return false;
		return JSON.stringify(filterValues) !== JSON.stringify(initialDraftRef.current);
	}, [filterValues]);

	const setField = useCallback(
		(key, value) => {
			setFilterValues((prev) => ({ ...prev, [key]: value }));
		},
		[setFilterValues],
	);

	const resetDraftFromUrl = useCallback(() => {
		setFilterValues(snapshotFromFilters(filters));
	}, [filters, setFilterValues]);

	const openModal = () => {
		const snapshot = snapshotFromFilters(filters);
		initialDraftRef.current = snapshot;
		setFilterValues(snapshot);
		setTagSearch("");
		setVisible(true);
	};

	const closeModal = useCallback(() => {
		resetDraftFromUrl();
		setTagSearch("");
		setVisible(false);
	}, [resetDraftFromUrl]);

	const handleApplyFilters = (event) => {
		event.preventDefault();
		filters?.changeAllFiltersAtOnce(filterValues);
		setVisible(false);
	};

	const handleClearFilters = () => {
		const params = filters?.allSearchParams
			?.filter((param) => !filters?.paginationParams?.includes(param))
			?.map((param) => ({ [param]: "" }))
			.reduce((acc, curr) => ({ ...acc, ...curr }), {});

		setFilterValues(params);
		filters?.changeAllFiltersAtOnce({ ...params });
		setVisible(false);
	};

	const onToggleGenre = useCallback(
		(name) => {
			const next = selectedGenres.includes(name)
				? selectedGenres.filter((genre) => genre !== name)
				: [...selectedGenres, name];
			setField("genreIs", joinTagFilter(next));
		},
		[selectedGenres, setField],
	);

	const onToggleTag = useCallback(
		(name) => {
			const next = selectedTags.includes(name)
				? selectedTags.filter((tag) => tag !== name)
				: [...selectedTags, name];
			setField("tagIs", joinTagFilter(next));
		},
		[selectedTags, setField],
	);

	const selectFields = [
		showPublisher && {
			id: "admin-filter-publisher",
			label: "Publisher",
			fieldKey: "publisherIs",
			value: filterValues?.publisherIs,
			placeholder: "All publishers",
			options: publisherOptions,
		},
		{
			id: "admin-filter-category",
			label: "Category",
			fieldKey: "categoryIs",
			value: filterValues?.categoryIs,
			placeholder: "All categories",
			options: categoryOptions,
		},
		showStatusPricing && {
			id: "admin-filter-pricing",
			label: "Pricing",
			fieldKey: "pricingModelIs",
			value: filterValues?.pricingModelIs,
			placeholder: "Any pricing",
			options: PRICING_OPTIONS,
		},
		showStatusPricing && {
			id: "admin-filter-active",
			label: "Activation",
			fieldKey: "isActive",
			value: filterValues?.isActive,
			placeholder: "Any status",
			options: ACTIVE_OPTIONS,
		},
		showStatusPricing && {
			id: "admin-filter-missing-files",
			label: "Missing files",
			fieldKey: "withoutFileCollection",
			value: filterValues?.withoutFileCollection,
			placeholder: "Any missing files",
			options: MISSING_FILE_OPTIONS,
		},
		showStatusPricing && {
			id: "admin-filter-available-files",
			label: "Available files",
			fieldKey: "withFileCollection",
			value: filterValues?.withFileCollection,
			placeholder: "Any available files",
			options: AVAILABLE_FILE_OPTIONS,
		},
	].filter(Boolean);

	const dialogHeader = (
		<div className="min-w-0 pr-8">
			<p className="text-xs font-semibold uppercase tracking-wide text-akgreener">Akello Library</p>
			<p className="mt-0.5 text-lg font-semibold text-akgreenest">Search and filter books</p>
			<p className="mt-1 text-sm font-normal text-gray-500">
				Narrow the catalogue to find the books you want.
			</p>
		</div>
	);

	const dialogFooter = (
		<div className="flex w-full flex-wrap items-center justify-between gap-3 pt-1">
			<div className="flex flex-wrap items-center gap-3">
				<p role="status" className="text-xs text-gray-500">
					{draftFilterCount} {draftFilterCount === 1 ? "filter selected" : "filters selected"}
				</p>
				<button
					type="button"
					onClick={handleClearFilters}
					className="h-11 px-2 text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-akgreen"
				>
					Clear
				</button>
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<button
					type="button"
					onClick={closeModal}
					className="inline-flex h-11 items-center gap-2 rounded-full border-2 border-akgreenest px-5 text-sm font-semibold text-akgreenest hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2"
				>
					<IoClose size={16} />
					Cancel
				</button>
				<button
					type="submit"
					form="admin-book-filter-form"
					className="h-11 rounded-full bg-akgreenest px-8 text-sm font-semibold text-white hover:bg-akgreener focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2"
				>
					Apply
				</button>
			</div>
		</div>
	);

	return (
		<>
			<button
				type="button"
				onClick={openModal}
				aria-expanded={visible}
				aria-haspopup="dialog"
				className={`relative flex h-11 min-w-max items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-akgreen ${
					numberOfActiveFilters > 0
						? "border-akgreenest bg-akgreenest bg-opacity-5 text-akgreenest"
						: "border-gray-300 bg-white text-gray-700 hover:border-akgreen"
				}`}
			>
				<FiFilter size={16} aria-hidden="true" />
				Advanced filter
				{numberOfActiveFilters > 0 && (
					<span className="flex h-5 portal-filter-badge items-center justify-center rounded-full bg-akgreenest px-1.5 text-xs font-bold text-white">
						{numberOfActiveFilters}
					</span>
				)}
			</button>

			<Dialog
				visible={visible}
				onHide={closeModal}
				modal
				blockScroll
				draggable={false}
				resizable={false}
				focusOnShow={false}
				dismissableMask={!isDirty}
				closeOnEscape
				position="center"
				header={dialogHeader}
				footer={dialogFooter}
				style={{ width: "min(48rem, 95vw)" }}
				className="admin-book-filter-dialog font-sans text-sm text-akgreenest rounded-3xl overflow-hidden"
				contentClassName="p-0"
				contentStyle={{ padding: 0 }}
			>
				<form
					id="admin-book-filter-form"
					onSubmit={handleApplyFilters}
					className="min-w-0 overflow-x-hidden px-5 pb-5 pt-1 sm:px-6"
					style={{ maxHeight: "min(60vh, 520px)", overflowY: "auto" }}
				>
					<div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-3">
						{selectFields.map((field, index) => (
							<div
								key={field.id}
								className={`border-gray-100 ${index % 2 === 0 ? "sm:border-r" : ""} ${
									index % 3 === 2 ? "lg:border-r-0" : "lg:border-r"
								}`}
							>
								<SheetSelect {...field} onChange={setField} />
							</div>
						))}
					</div>

					<div className="mt-6 space-y-5">
						<PillChoices
							legend="Collections"
							items={genreList}
							selected={selectedGenres}
							onToggle={onToggleGenre}
							searchLabel="Search collections"
							emptyMessage="No collections yet"
							noMatchMessage="No collections match your search"
							totalCount={genreList.length}
						/>
						<PillChoices
							legend="Tags"
							items={pagedTags.records}
							selected={selectedTags}
							onToggle={onToggleTag}
							searchLabel="Search tags"
							emptyMessage="No tags yet"
							loadingMessage="Loading tags…"
							noMatchMessage="No tags match your search"
							remote
							onSearchChange={setTagSearch}
							hasMore={pagedTags.hasMorePages}
							onLoadMore={pagedTags.loadMore}
							isLoadingMore={
								pagedTags.isLoadingMore || (pagedTags.isFetching && pagedTags.records.length === 0)
							}
							totalCount={pagedTags.totalRecords}
						/>
					</div>
				</form>
			</Dialog>
		</>
	);
};

export default BookFilter;
